import type { Invite } from '@puppy-doctor/platform';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { acceptInvite } from '@/api/invites.ts';
import { Button, Panel } from '@/components/ui.tsx';
import { expiryOf, useNow } from '@/lib/expiry.ts';

/**
 * The invitations waiting for the person looking at the screen.
 *
 * Drawn in TWO places, and the second one is the gap this used to have: it lives
 * on the onboarding screen, where someone who belongs to no kennel yet accepts
 * their first invitation — and on the home screen, because a vet who already
 * belongs to one kennel and is then invited to a second had NO way of finding
 * out. Onboarding never renders for them (they have a tenant), so the invitation
 * sat in the database being visible to nobody but the owner who sent it.
 *
 * Nothing here is drawn when there is nothing waiting, which is almost always:
 * this is a rare event, and an empty "no invitations" block on the home screen
 * every day would be furniture.
 */
export const PendingInvites = ({ invites }: { invites: readonly Invite[] }) => {
  const { t } = useTranslation();

  if (invites.length === 0) return null;

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-lg font-bold tracking-[-0.01em]">
        {t('invite.waiting', { count: invites.length })}
      </h2>
      {invites.map((invite) => (
        <InviteCard key={invite.id} invite={invite} />
      ))}
    </section>
  );
};

const InviteCard = ({ invite }: { invite: Invite }) => {
  const { t } = useTranslation();
  const [joining, setJoining] = useState(false);
  const [failed, setFailed] = useState(false);
  const { expired, daysLeft } = expiryOf(invite.expiresAt, useNow());

  const join = () => {
    setFailed(false);
    setJoining(true);
    acceptInvite(invite.id).catch(() => {
      // The component stays mounted on failure — success unmounts it, because
      // the membership arrives through TenantProvider's listener and there is
      // no longer an onboarding screen to be on.
      setJoining(false);
      setFailed(true);
    });
  };

  return (
    <Panel className="flex flex-col gap-3">
      <div>
        <p className="text-lg font-semibold">{invite.tenantName}</p>
        {/* No role badge beside this: the sentence already names the role, and
            saying it twice in one card reads as two different facts. */}
        <p className="text-sm text-ink-soft">
          {t('invite.asRole', { role: t(`role.${invite.role}`).toLocaleLowerCase() })}
        </p>
      </div>

      <p className="text-sm text-ink-faint">
        {expired ? t('invite.expired') : t('invite.expiresIn', { count: daysLeft })}
      </p>

      {!expired && (
        <Button full onClick={join} disabled={joining}>
          {joining ? t('invite.accepting') : t('invite.accept')}
        </Button>
      )}

      {failed && (
        <p role="alert" className="text-sm text-alert">
          {t('invite.acceptFailed')}
        </p>
      )}
    </Panel>
  );
};
